import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, AlertTriangle, Shield, ShieldAlert, ShieldX, Info, Mail, MailX } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SecurityAlert {
  id: string;
  created_at: string;
  source: string;
  event_type: string;
  severity: string;
  action: string;
  reason_code: string;
  user_id: string | null;
  conversation_id: string | null;
  message_id: string | null;
  thinking_method_id: string | null;
  snippet: string | null;
  details: Record<string, unknown>;
  email_sent: boolean;
  email_sent_at: string | null;
  email_error: string | null;
}

const SEVERITY_CONFIG: Record<string, { icon: React.ElementType; class: string }> = {
  critical: { icon: ShieldX, class: 'border-red-500/50 bg-red-500/10 text-red-400' },
  high: { icon: ShieldAlert, class: 'border-orange-500/50 bg-orange-500/10 text-orange-400' },
  medium: { icon: AlertTriangle, class: 'border-yellow-500/50 bg-yellow-500/10 text-yellow-400' },
  low: { icon: Info, class: 'border-blue-500/50 bg-blue-500/10 text-blue-400' },
};

const ACTION_CONFIG: Record<string, string> = {
  blocked: 'border-red-500/50 bg-red-500/10 text-red-400',
  allowed_with_warning: 'border-yellow-500/50 bg-yellow-500/10 text-yellow-400',
  rejected: 'border-orange-500/50 bg-orange-500/10 text-orange-400',
};

export const SecurityAlerts = () => {
  const { toast } = useToast();
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchAlerts = async () => {
    setLoading(true);
    let query = supabase
      .from('security_alerts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200);

    if (severityFilter !== 'all') query = query.eq('severity', severityFilter);
    if (actionFilter !== 'all') query = query.eq('action', actionFilter);

    const { data, error } = await query;
    if (error) {
      toast({ title: 'Error', description: 'Failed to fetch security alerts', variant: 'destructive' });
    } else {
      setAlerts((data as SecurityAlert[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchAlerts(); }, [severityFilter, actionFilter]);

  const filtered = alerts.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      a.event_type.toLowerCase().includes(q) ||
      a.reason_code.toLowerCase().includes(q) ||
      a.source.toLowerCase().includes(q) ||
      a.snippet?.toLowerCase().includes(q) ||
      a.user_id?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Shield className="h-5 w-5" /> Security Alerts
          </h2>
          <p className="text-muted-foreground text-sm">{filtered.length} alert{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAlerts} disabled={loading}>
          <RefreshCw className={cn("h-4 w-4 mr-1", loading && "animate-spin")} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <Input
          placeholder="Search event, reason, source, user..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="max-w-xs"
        />
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
            <SelectItem value="allowed_with_warning">Warning</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Alerts list */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading alerts...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No security alerts found.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map(alert => {
            const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.low;
            const SevIcon = sev.icon;
            const expanded = expandedId === alert.id;

            return (
              <Card
                key={alert.id}
                className="border-border/50 bg-card/50 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedId(expanded ? null : alert.id)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <SevIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-foreground">{alert.event_type}</span>
                        <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 capitalize", sev.class)}>
                          {alert.severity}
                        </Badge>
                        <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 capitalize", ACTION_CONFIG[alert.action])}>
                          {alert.action.replace(/_/g, ' ')}
                        </Badge>
                        {alert.email_sent ? (
                          <Mail className="h-3 w-3 text-emerald-400" />
                        ) : alert.email_error ? (
                          <MailX className="h-3 w-3 text-red-400" />
                        ) : null}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {alert.source} · {alert.reason_code} · {new Date(alert.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {expanded && (
                    <div className="mt-3 pt-3 border-t border-border/50 space-y-2 text-xs">
                      <div className="grid grid-cols-2 gap-2">
                        <div><span className="text-muted-foreground">User ID:</span> <span className="font-mono text-foreground">{alert.user_id || '—'}</span></div>
                        <div><span className="text-muted-foreground">Conversation:</span> <span className="font-mono text-foreground">{alert.conversation_id?.slice(0, 8) || '—'}</span></div>
                        <div><span className="text-muted-foreground">Message:</span> <span className="font-mono text-foreground">{alert.message_id?.slice(0, 8) || '—'}</span></div>
                        <div><span className="text-muted-foreground">Lens:</span> <span className="font-mono text-foreground">{alert.thinking_method_id?.slice(0, 8) || '—'}</span></div>
                      </div>
                      {alert.snippet && (
                        <div>
                          <span className="text-muted-foreground">Snippet:</span>
                          <pre className="mt-1 p-2 rounded bg-muted/50 whitespace-pre-wrap break-words font-mono text-foreground">{alert.snippet}</pre>
                        </div>
                      )}
                      {Object.keys(alert.details).length > 0 && (
                        <div>
                          <span className="text-muted-foreground">Details:</span>
                          <ScrollArea className="max-h-[200px] mt-1">
                            <pre className="p-2 rounded bg-muted/50 whitespace-pre-wrap break-words font-mono text-foreground">
                              {JSON.stringify(alert.details, null, 2)}
                            </pre>
                          </ScrollArea>
                        </div>
                      )}
                      {alert.email_error && (
                        <div className="text-red-400">
                          <span className="text-muted-foreground">Email error:</span> {alert.email_error}
                        </div>
                      )}
                      {alert.email_sent_at && (
                        <div><span className="text-muted-foreground">Email sent:</span> {new Date(alert.email_sent_at).toLocaleString()}</div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
